import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'

export default class extends Controller {
  static targets = [
    'form',
    'option',
    'totalFee',
    'optionFee',
    'optionFeeInput',
    'email',
    'startDate',
    'endDate',
    'planId',
    'errorModal',
    'country',
    'postcode',
    'prefectureForm',
    'prefecture',
    'municipalities',
    'town',
    'plannedParticipationSelect', // 保育園留学種別の入力フィールド
    'allergySelect', // 食物アレルギー有無の入力フィールド
    'allergenicFoodsGroup', // アレルギー食品
    'allergenicFoods', // アレルギー食品の入力フィールド
    'otherFoodInput', // その他食材の入力フィールド
    'nonfoodAllergySelect', // 食物以外のアレルギー有無の入力フィールド
    'allergyCauses', // アレルギーの原因となるも
    'allergyCausesInput', // アレルギーの原因となるもの入力フィールド
    'symptoms', // アレルギーの症状
    'symptomsInput', // アレルギーの症状の入力フィールド
    'foodrequestSelect', // 食事に関する配慮が必要かどうかの入力フィールド
    'foodrequest', // 食事についてスペシャルリクエスト
    'foodrequestInput', // 食事についてスペシャルリクエストの入力フィールド
    'allergyElimination', // 留学先で除去食の対応
    'allergyEliminationInput', // 留学先で除去食の対応の入力フィールド
    'developmentaldisorderSelect', // 発達障がいの診断歴有無の入力フィールド
    'thingsThatNeedAttention', // 発達の遅れや集団生活を送る上で配慮が必要なこと
    'diagnosis', // 診断名
    'diagnosisInput', // 診断名の入力フィールド
    'medicalHistorySelect', // 既往歴の入力フィールド
    'medicalHistoryDetails', // 既往歴詳細
    'medicalHistoryDetailsInput', // 既往歴詳細の入力フィールド
    'question1', // 除去食の対応の確認の入力フィールド
    'opportunitynurseryschool', // 過去3日以上の通園経験の有無
    'opportunitynurseryschoolInput', // 過去3日以上の通園経験の有無の入力フィールド
    'preschoolInfo', // 保育園・幼稚園情報（opportunitynurseryschoolでYESの場合に表示）
    'preschoolNameInput', // 保育園・幼稚園名の入力フィールド
    'preschoolUrlInput', // 保育園・幼稚園URLの入力フィールド
    'schoolType', // 在籍学校の種類
    'schoolTypeInput', // 在籍学校の種類の入力フィールド
    'schoolTitle', // 在籍学校のタイトル
    'schoolName', // 在籍している学校名
    'schoolNameInput', // 在籍している学校名の入力フィールド
    'fullCapacityModal',
  ]

  static values = {
    baseFee: String,
    allergy: Boolean,
    subAllergy: Boolean,
    schoolExperience: Boolean,
  }

  connect() {
    this.optionChange()
    this.postcodeTarget.addEventListener('input', () => {
      if (i18n.locale === 'ja' || (i18n.locale === 'en' && this.countryTarget.value === 'Japan')) {
        this.fetchAddress()
      }
    })

    // 留学家族のこころえに関するモーダルを表示
    this.setupAgreeModal()

    // 保育園留学種別を選択時、他フォームの表示/非表示を切り替え
    this.plannedParticipationSelectTargets.forEach((selectElement, index) => {
      // 保育園留学を選択した際に表示するフォーム
      const nurseryDisplayElements = [
        { displayElement: this.question1Targets[index], requiredElement: this.question1Targets[index] },
        {
          displayElement: this.opportunitynurseryschoolTargets[index],
          requiredElement: this.opportunitynurseryschoolInputTargets[index],
        },
      ]
      // 小学生留学を選択した際に表示するフォーム
      const elementaryDisplayElements = [
        { displayElement: this.schoolTitleTargets[index] },
        { displayElement: this.schoolTypeTargets[index], requiredElement: this.schoolTypeInputTargets[index] },
        { displayElement: this.schoolNameTargets[index], requiredElement: this.schoolNameInputTargets[index] },
      ]
      this.setupToggleDisplayForSchoolType(selectElement, nurseryDisplayElements, elementaryDisplayElements)
    })

    // 食物アレルギー有無選択時、他フォームの表示/非表示を切り替え
    this.allergySelectTargets.forEach((selectElement, index) => {
      this.setupToggleDisplay(selectElement, this.allergenicFoodsGroupTargets[index], this.allergenicFoodsGroupTargets[index])
      this.setupToggleSymptomsDisplay(selectElement)
      this.setupToggleAllergyEliminationDisplay(selectElement)
      selectElement.addEventListener('change', () => {
        this.handleAllergySelection(index)
      })
    })

    // 食物以外のアレルギー有無の選択時、他フォームの表示/非表示を切り替え
    this.nonfoodAllergySelectTargets.forEach((selectElement, index) => {
      this.setupToggleDisplay(selectElement, this.allergyCausesTargets[index], this.allergyCausesInputTargets[index])
      this.setupToggleSymptomsDisplay(selectElement)
    })

    // 食事に関する配慮が必要かどうかの選択時、他フォームの表示/非表示を切り替え
    this.foodrequestSelectTargets.forEach((selectElement, index) => {
      this.setupToggleDisplay(selectElement, this.foodrequestTargets[index], this.foodrequestInputTargets[index])
      this.setupToggleAllergyEliminationDisplay(selectElement)
    })

    // 週３日以上の通園経験の有無選択時
    if (this.schoolExperienceValue) {
      this.opportunitynurseryschoolInputTargets.forEach((selectElement, index) => {
        selectElement.addEventListener('change', () => {
          const selectElement = this.opportunitynurseryschoolInputTargets[index]
          if (selectElement.value === '無') {
            const daycareExperienceModal = document.getElementById('daycare-experience-modal')
            if (daycareExperienceModal) {
              daycareExperienceModal.classList.remove('hidden')
            }
            const message = i18n.t('reservation.daycare_experience_modal.validation_text')
            this.addErrorMessage(selectElement, false, false, 'validation-daycare-experience', message)
          } else if (selectElement.value === '有') {
            this.removeErrorMessage(selectElement, '.validation-daycare-experience')
          }
        })
      })
    }

    // 発達障がいの診断歴有無の選択時、他フォームの表示/非表示を切り替え
    this.developmentaldisorderSelectTargets.forEach((selectElement, index) => {
      this.setupToggleDisplay(selectElement, this.diagnosisTargets[index], this.diagnosisInputTargets[index])
    })

    // 既往歴の有無の選択時、他フォームの表示/非表示を切り替え
    this.medicalHistorySelectTargets.forEach((selectElement, index) => {
      this.setupToggleDisplay(
        selectElement,
        this.medicalHistoryDetailsTargets[index],
        this.medicalHistoryDetailsInputTargets[index],
      )
    })

    // 過去3日以上の通園経験の有無の選択時、保育園・幼稚園情報フォームの表示/非表示を切り替え
    this.opportunitynurseryschoolInputTargets.forEach((selectElement, index) => {
      this.setupTogglePreschoolInfo(selectElement, index)
    })

    // アレルギー食品リストで「その他」を選択時、「その他」に関する入力欄の表示/非表示を切り替え
    this.allergenicFoodsGroupTargets.forEach((group, groupIndex) => {
      group.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          this.toggleOtherFoodInput(groupIndex)
          this.handleOtherFoodCheckbox(groupIndex)
        })
      })
    })

    // ページ読み込み時の初期化処理
    this.allergenicFoodsGroupTargets.forEach((_, groupIndex) => {
      this.toggleOtherFoodInput(groupIndex)
    })
    this.toggleSymptomsDisplay()
    this.toggleAllergyEliminationDisplay() // ページ読み込み時に除去食対応の状態を初期化

    this.setupAccordion()
    // jsが読み込まれる前に「確認へすすむ」ボタンが押せてしまい、
    // たまにバリデーション処理が実行されない問題を回避するため、
    // jsが読み込まれた後にボタンを有効化する
    this.enableSubmitButton()

    // 画面レンダリング時に各アコーディオンのバリデーション状態を更新
    document.querySelectorAll('.accordion-content').forEach((content) => {
      this.checkFormInputs(content)
    })

    // フォーム入力時のリアルタイムバリデーション設定
    this.setupRealTimeValidation()

    // 数字のみフィールドのリアルタイムバリデーション設定
    this.setupNumberOnlyValidation()
  }

  setupAgreeModal() {
    const modal = document.querySelector('.heart_modal')
    const agreeCheckbox = document.getElementById('agree_checkbox')
    const holidayCheckbox = document.getElementById('holiday_checkbox')
    const modalButton = document.getElementById('modal_button')

    // チェックボックス状態を確認する関数
    const checkModalState = () => {
      const agreeChecked = agreeCheckbox.checked
      const holidayChecked = holidayCheckbox ? holidayCheckbox.checked : true // 祝日チェックボックスがない場合はtrue

      if (agreeChecked && holidayChecked) {
        modalButton.disabled = false // すべてチェックされたら「予約へ進む」ボタンを有効化
        modalButton.classList.add('background-green-primary', 'text-white')
      } else {
        modalButton.disabled = true // どれかがチェック外れたら再び無効化
        modalButton.classList.remove('background-green-primary', 'text-white')
      }
    }

    // ガイドライン同意チェックボックスのイベントリスナー
    agreeCheckbox.addEventListener('change', checkModalState)

    // 祝日チェックボックスのイベントリスナー（存在する場合のみ）
    if (holidayCheckbox) {
      holidayCheckbox.addEventListener('change', checkModalState)
    }

    // 「予約へ進む」ボタンがクリックされたらモーダルを閉じる
    modalButton.addEventListener('click', function () {
      modal.style.display = 'none' // モーダルを非表示にする
    })

    checkModalState()
  }

  setupAccordion() {
    const accordionHeaders = document.querySelectorAll('.accordion-header')

    accordionHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        header.classList.toggle('active')
        const content = header.nextElementSibling

        // 他のすべてのアコーディオンコンテンツを閉じる
        document.querySelectorAll('.accordion-content').forEach((otherContent) => {
          if (otherContent !== content) {
            otherContent.classList.add('hidden')
            const otherHeader = otherContent.previousElementSibling
            otherHeader.classList.remove('active')
          }
        })

        document.querySelectorAll('.accordion-content').forEach((content) => {
          this.checkFormInputs(content)
        })

        // クリックされたアコーディオンコンテンツをトグル
        content.classList.toggle('hidden')

        // アコーディオンが開かれた場合にスクロール
        if (!content.classList.contains('hidden')) {
          // スクロール先の要素を取得
          const scrollElement = header.parentElement.previousElementSibling
          scrollElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    })
  }

  checkFormInputs(content) {
    const inputFields = Array.from(content.querySelectorAll('.input-check'))
    const selectFields = Array.from(content.querySelectorAll('.select-check'))
    const radioGroups = Array.from(content.querySelectorAll('.radio-check'))
    const checkboxGroups = Array.from(content.querySelectorAll('.checkbox-check'))

    let allFilled = true
    let hasFormatErrors = false
    let firstInvalidField = null

    inputFields.forEach((field) => {
      const isEmpty = !field.value.trim()
      const isRequired = field.classList.contains('required')

      if (isRequired && isEmpty) {
        allFilled = false
        if (!firstInvalidField) firstInvalidField = field
      }

      // 郵便番号や電話番号の形式チェック（値が入力されている場合のみ）
      if (field.value.trim() && (field.classList.contains('post-code') || field.classList.contains('phone-number'))) {
        if (!this.isValidInput(field.value.trim())) {
          allFilled = false
          hasFormatErrors = true
          if (!firstInvalidField) firstInvalidField = field
        }
      }
    })

    // 既存のエラーメッセージが表示されている場合はエラー状態とする
    const errorMessages = content.querySelectorAll('.validation-warning')
    if (errorMessages.length > 0) {
      allFilled = false
      hasFormatErrors = true
    }

    selectFields.forEach((field) => {
      if ((field.classList.contains('required') || field.classList.contains('date-selection')) && !field.value) {
        allFilled = false
        if (!firstInvalidField) firstInvalidField = field
      }
    })

    checkboxGroups.forEach((group) => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]')
      const isChecked = Array.from(checkboxes).some((checkbox) => checkbox.checked)
      if (group.classList.contains('required') && !isChecked) {
        allFilled = false
        if (!firstInvalidField) firstInvalidField = group
      }
    })

    radioGroups.forEach((group) => {
      const radioFields = group.querySelectorAll('input[type="radio"]')
      const isChecked = Array.from(radioFields).some((field) => field.checked)
      if (group.classList.contains('required') && !isChecked) {
        allFilled = false
        if (!firstInvalidField) firstInvalidField = group
      }
    })

    let message = ''

    if (content.querySelector('.first_name')) {
      // `.first_name` と `.last_name` の値を取得
      const firstNameInput = content.querySelector('.first_name')
      const lastNameInput = content.querySelector('.last_name')

      // 各フィールドの値を取得し、存在しない場合は空文字にする
      const firstName = firstNameInput ? firstNameInput.value : ''
      const lastName = lastNameInput ? lastNameInput.value : ''

      const fullName = i18n.locale === 'ja' ? `${lastName} ${firstName}`.trim() : `${firstName} ${lastName}`.trim()

      message = fullName || i18n.t('reservation_input.accordion.message_not_input')
    } else if (content.querySelector('.representative_email')) {
      const emailInput = content.querySelector('.representative_email')
      const email = emailInput ? emailInput.value : ''

      message = email || i18n.t('reservation_input.accordion.message_not_input')
    } else {
      Array.from(document.querySelectorAll('.option-item-info')).forEach((item) => {
        // option_nameの値を取得
        const optionNameElement = item.querySelector('.option-item-left p')
        const optionName = optionNameElement ? optionNameElement.textContent.trim() : ''

        // 選択された項目の金額を取得
        const optionPriceElement = item.querySelector('.option-price')
        const optionPrice = optionPriceElement.getAttribute('data-option-price')

        // 選択されたselectの値を取得
        const selectElement = item.querySelector('.option-item-right select')
        const selectedValue = selectElement ? selectElement.options[selectElement.selectedIndex].value : ''

        // selectedValueが"0"の場合は無視
        if (selectedValue === '') {
          return null
        }

        // すでにメッセージがあれば改行タグを追加してからメッセージを追加
        if (message) {
          message += '<br>' // 既存のメッセージがある場合は改行
        }

        // メッセージを連結
        message += `${optionName} ¥${optionPrice} × ${selectedValue}`
      })

      message ||= i18n.t('reservation_input.accordion.message_no_selected')
    }

    const messageElement = content.previousElementSibling.querySelector('.message')
    const notifyElement = content.previousElementSibling.querySelector('.notify')
    const ageDisplayElement = content.previousElementSibling.querySelector('.age-display')

    // 子供情報の場合、年齢を計算して表示
    if (content.querySelector('.first_name') && ageDisplayElement) {
      this.updateAgeDisplay(content, ageDisplayElement)
    }

    if (allFilled) {
      notifyElement.querySelector('.icon').classList.remove('alert-icon')
      notifyElement.querySelector('.icon').classList.add('success-icon')
      notifyElement.querySelector('.icon-message').textContent = ''
      messageElement.innerHTML = message
    } else {
      notifyElement.querySelector('.icon').classList.remove('success-icon')
      notifyElement.querySelector('.icon').classList.add('alert-icon')
      // 形式エラーがある場合は「項目に不備があります」、そうでなければ「未入力の項目があります」
      const errorMessageKey = hasFormatErrors
        ? 'reservation_input.accordion.icon_message'
        : 'reservation_input.accordion.icon_message_missing'
      notifyElement.querySelector('.icon-message').textContent = i18n.t(errorMessageKey)
      messageElement.innerHTML = message
    }
  }

  setupRealTimeValidation() {
    // 各アコーディオンコンテンツ内のフォーム要素にイベントリスナーを追加
    document.querySelectorAll('.accordion-content').forEach((content) => {
      // input要素のイベントリスナー
      content.querySelectorAll('.input-check').forEach((input) => {
        ;['input', 'blur'].forEach((eventType) => {
          input.addEventListener(eventType, () => {
            this.checkFormInputs(content)
          })
        })
      })

      // select要素のイベントリスナー
      content.querySelectorAll('.select-check').forEach((select) => {
        select.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      })

      // checkbox要素のイベントリスナー
      content.querySelectorAll('.checkbox-check input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      })

      // radio要素のイベントリスナー
      content.querySelectorAll('.radio-check input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      })

      // 生年月日フィールドのイベントリスナー（年齢表示用）
      const yearSelect = content.querySelector('select[name="fronts_reservation[children_birth_year][]"]')
      const monthSelect = content.querySelector('select[name="fronts_reservation[children_birth_month][]"]')
      const daySelect = content.querySelector('select[name="fronts_reservation[children_birth_day][]"]')

      if (yearSelect) {
        yearSelect.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      }
      if (monthSelect) {
        monthSelect.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      }
      if (daySelect) {
        daySelect.addEventListener('blur', () => {
          this.checkFormInputs(content)
        })
        daySelect.addEventListener('change', () => {
          this.checkFormInputs(content)
        })
      }
    })
  }

  setupNumberOnlyValidation() {
    // 数字のみ入力フィールドのリアルタイムバリデーション（郵便番号・電話番号）
    document.querySelectorAll('input.number-only').forEach((field) => {
      field.addEventListener('input', () => {
        const validationClass = field.classList.contains('post-code') ? '.validation-postcode-number' : '.validation-phone-number'

        this.removeErrorMessage(field, validationClass)

        if (field.value.trim() && !this.isValidInput(field.value.trim())) {
          const fieldName =
            field.getAttribute('data-validation-name') ||
            (field.classList.contains('post-code')
              ? i18n.t('reservation_input.representative.address.postal_code')
              : 'フィールド')
          const message = i18n.t('message.error.input.valid_enter', { field_name: fieldName })
          const messageClass = field.classList.contains('post-code') ? 'validation-postcode-number' : 'validation-phone-number'

          this.addErrorMessage(field, false, false, messageClass, message)
        }
      })
    })
  }

  closeAllAccordions() {
    // すべてのアコーディオンヘッダーを非アクティブにする
    document.querySelectorAll('.accordion-header').forEach((header) => {
      header.classList.remove('active')
    })

    // すべてのアコーディオンコンテンツを隠す
    document.querySelectorAll('.accordion-content').forEach((content) => {
      content.classList.add('hidden')
    })
  }

  openFirstInvalidAccordion() {
    // エラーメッセージが表示されているnotify要素を検索
    const formatErrorMessage = i18n.t('reservation_input.accordion.icon_message')
    const missingFieldsMessage = i18n.t('reservation_input.accordion.icon_message_missing')
    const invalidNotifies = Array.from(document.querySelectorAll('.notify .icon-message')).filter((element) => {
      const text = element.textContent.trim()
      return text === formatErrorMessage || text === missingFieldsMessage
    })

    if (invalidNotifies.length > 0) {
      // 最初の不備があるnotify要素から対応するアコーディオンを見つける
      const firstInvalidNotify = invalidNotifies[0]
      const accordionHeader = firstInvalidNotify.closest('.accordion-header')

      if (accordionHeader) {
        const accordionContent = accordionHeader.nextElementSibling

        if (accordionContent && accordionContent.classList.contains('accordion-content')) {
          // アコーディオンを開く
          accordionHeader.classList.add('active')
          accordionContent.classList.remove('hidden')

          // アコーディオンの位置までスクロール
          const scrollElement = accordionHeader.parentElement.previousElementSibling
          if (scrollElement) {
            scrollElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }
      }
    }
  }

  handleOtherFoodCheckbox(groupIndex) {
    const group = this.allergenicFoodsGroupTargets[groupIndex]
    const otherCheckbox = group.querySelector('input[type="checkbox"][value="その他"]')
    const otherFoodInput = this.otherFoodInputTargets[groupIndex]?.querySelector('input')

    // 'その他' のチェックが外れた場合、テキストボックスの値をクリア
    if (otherCheckbox && otherFoodInput && !otherCheckbox.checked) {
      otherFoodInput.value = ''
    }
  }

  handleAllergySelection(index) {
    const selectElement = this.allergySelectTargets[index]
    const allergenicFoods = this.allergenicFoodsGroupTargets[index].querySelectorAll('input[type="checkbox"]')
    const otherFoodInput = this.otherFoodInputTargets[index]

    // '無' が選択されたらすべてのチェックを外す
    if (selectElement.value === '無') {
      allergenicFoods.forEach((checkbox) => {
        checkbox.checked = false
      })

      // その他の入力欄をクリアして非表示にする（存在チェック）
      if (otherFoodInput) {
        const input = otherFoodInput.querySelector('input')
        if (input) input.value = ''
        otherFoodInput.style.display = 'none'
      }
    } else if (selectElement.value === '有') {
      // 生年月日から年齢を計算し、適切なallergyValueを選択
      const age = this.calculateAgeFromBirthday(index)
      let shouldShowModal = false

      if (age !== null) {
        // 5歳以下は保育園留学対象、6歳以上は小学生留学対象
        if (age <= 5) {
          shouldShowModal = this.allergyValue
        } else if (age >= 6) {
          shouldShowModal = this.subAllergyValue
        }
      } else {
        // 生年月日が未入力の場合は、デフォルトで保育園留学施設の制約を使用
        shouldShowModal = this.allergyValue
      }

      // アレルギーが「有」で、該当施設に制約がある場合、食物アレルギーに関するモーダルを表示
      if (shouldShowModal) {
        const foodAllergyModal = document.getElementById('food-allergy-modal')
        if (foodAllergyModal) {
          foodAllergyModal.classList.remove('hidden')
        }
      }
    }

    // アレルギーの原因となる食品の表示・非表示を切り替える
    this.toggleDisplay(selectElement, this.allergenicFoodsGroupTargets[index])

    // チェックボックスの状態を確認し、「その他」が選択されていれば表示する
    this.toggleOtherFoodInput(index)
  }

  // 生年月日から学年度基準の年齢を計算（age_calculator.rbのcalculate_age_for_monthと同等のロジック）
  calculateAgeFromBirthday(index) {
    // 該当する子供の生年月日フォーム要素を取得
    const birthdaySection = this.allergySelectTargets[index].closest('.accordion-content')
    if (!birthdaySection) return null

    const yearSelect = birthdaySection.querySelector('select[name="fronts_reservation[children_birth_year][]"]')
    const monthSelect = birthdaySection.querySelector('select[name="fronts_reservation[children_birth_month][]"]')
    const daySelect = birthdaySection.querySelector('select[name="fronts_reservation[children_birth_day][]"]')

    if (!yearSelect || !monthSelect || !daySelect) return null

    const year = parseInt(yearSelect.value)
    const month = parseInt(monthSelect.value)
    const day = parseInt(daySelect.value)

    if (!year || !month || !day) return null

    // 現在の学年度を計算（age_calculator.rb line 32-35と同等）
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1 // getMonth()は0-11なので+1
    const currentSchoolYear = currentMonth >= 4 ? currentYear : currentYear - 1

    // 学年度の4月1日を計算（age_calculator.rb line 53）
    const schoolStartDate = new Date(currentSchoolYear, 3, 1) // 4月1日（月は0-11なので3）

    // 誕生日のDateオブジェクトを作成（うるう年対応: age_calculator.rb line 40-49）
    let birthDate
    if (month === 2 && day === 29) {
      // うるう年の2月29日生まれの場合
      try {
        birthDate = new Date(year, month - 1, day)
        if (birthDate.getDate() !== day) {
          // 無効な日付の場合は3月1日として扱う
          birthDate = new Date(year, 2, 1)
        }
      } catch (e) {
        birthDate = new Date(year, 2, 1)
      }
    } else {
      birthDate = new Date(year, month - 1, day)
    }

    // 4月1日時点の満年齢を計算（age_calculator.rb line 56）
    let age = schoolStartDate.getFullYear() - birthDate.getFullYear()

    // 誕生日がまだ来ていない場合は1歳引く（age_calculator.rb line 59-65）
    let birthdayThisYear
    if (birthDate.getMonth() === 1 && birthDate.getDate() === 29 && !this.isLeapYear(currentSchoolYear)) {
      // うるう年の2月29日生まれの場合、非うるう年では3月1日として扱う
      birthdayThisYear = new Date(currentSchoolYear, 2, 1)
    } else {
      birthdayThisYear = new Date(currentSchoolYear, birthDate.getMonth(), birthDate.getDate())
    }

    if (birthdayThisYear > schoolStartDate) {
      age -= 1
    }

    // 負の年齢を防ぐ（age_calculator.rb line 67）
    return Math.max(age, 0)
  }

  // うるう年判定
  isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  calculateCurrentAge(year, month, day) {
    if (!year || !month || !day) return null

    const birthDate = new Date(year, month - 1, day)
    const today = new Date()

    let years = today.getFullYear() - birthDate.getFullYear()
    let months = today.getMonth() - birthDate.getMonth()

    if (months < 0) {
      years--
      months += 12
    } else if (months === 0 && today.getDate() < birthDate.getDate()) {
      years--
      months = 11
    } else if (today.getDate() < birthDate.getDate()) {
      months--
      if (months < 0) {
        years--
        months = 11
      }
    }

    return { years, months }
  }

  updateAgeDisplay(content, ageDisplayElement) {
    const yearSelect = content.querySelector('select[name="fronts_reservation[children_birth_year][]"]')
    const monthSelect = content.querySelector('select[name="fronts_reservation[children_birth_month][]"]')
    const daySelect = content.querySelector('select[name="fronts_reservation[children_birth_day][]"]')

    if (!yearSelect || !monthSelect) {
      ageDisplayElement.textContent = ''
      return
    }

    const year = parseInt(yearSelect.value)
    const month = parseInt(monthSelect.value)
    const day = parseInt(daySelect.value)

    const age = this.calculateCurrentAge(year, month, day)

    if (age) {
      if (i18n.locale === 'ja') {
        ageDisplayElement.textContent = `${age.years}歳${age.months}ヶ月`
      } else {
        // 英語表示
        let ageText = ''
        if (age.years > 0) {
          const yearText = age.years === 1 ? 'year' : 'years'
          ageText += `${age.years} ${yearText}`
        }
        if (age.months > 0 || age.years === 0) {
          const monthText = age.months === 1 ? 'month' : 'months'
          if (ageText) ageText += ' '
          ageText += `${age.months} ${monthText}`
        }
        ageDisplayElement.textContent = ageText
      }
    } else {
      ageDisplayElement.textContent = ''
    }
  }

  toggleDisplayForSchoolType(selectElement, nurserySchoolElements, elementarySchoolElements) {
    const selectedValue = selectElement.value

    if (selectedValue === '保育園留学') {
      nurserySchoolElements.forEach((element) => {
        element.displayElement.style.display = 'block'
        element.requiredElement?.classList.add('required')
      })
      elementarySchoolElements.forEach((element) => {
        element.displayElement.style.display = 'none'
        element.requiredElement?.classList.remove('required')
      })
    } else if (selectedValue === '小学生留学') {
      nurserySchoolElements.forEach((element) => {
        element.displayElement.style.display = 'none'
        element.requiredElement?.classList.remove('required')
      })
      elementarySchoolElements.forEach((element) => {
        element.displayElement.style.display = 'block'
        element.requiredElement?.classList.add('required')
      })
    }
  }

  setupToggleDisplayForSchoolType(selectElement, nurserySchoolElements, elementarySchoolElements) {
    selectElement.addEventListener('change', () => {
      this.toggleDisplayForSchoolType(selectElement, nurserySchoolElements, elementarySchoolElements)
    })

    // 初期化
    this.toggleDisplayForSchoolType(selectElement, nurserySchoolElements, elementarySchoolElements)
  }

  // 共通化された表示/非表示処理
  toggleDisplay(selectElement, targetElement, requiredElement = null) {
    // '有' の場合に表示し、'無' の場合に非表示
    if (selectElement.value === '有') {
      if (requiredElement) requiredElement.classList.add('required')
      targetElement.style.display = 'block'
    } else {
      if (requiredElement) requiredElement.classList.remove('required')
      targetElement.style.display = 'none'
    }

    // '無' が選択された場合、input 要素の値をクリア
    // アレルギーの症状（symptoms）は、toggleSymptoms()で削除するため、ここでリセットしない
    if (selectElement.value === '無') {
      const inputsToClear = targetElement.querySelector('input')
      const textareasToClear = targetElement.querySelector('textarea')

      if (inputsToClear) inputsToClear.value = ''
      if (textareasToClear && !textareasToClear.classList.contains('symptoms')) textareasToClear.value = ''
    }
  }

  // 表示/非表示のイベントリスナーを追加
  setupToggleDisplay(selectElement, targetElement, requiredElement = null) {
    selectElement.addEventListener('change', () => {
      this.toggleDisplay(selectElement, targetElement, requiredElement)
    })

    // 初期化
    this.toggleDisplay(selectElement, targetElement)
  }

  // アレルギーの症状の表示/非表示を切り替える
  toggleSymptomsDisplay() {
    this.symptomsTargets.forEach((symptomsElement, index) => {
      const allergyPresent =
        this.allergySelectTargets[index]?.value === '有' || this.nonfoodAllergySelectTargets[index]?.value === '有'

      if (allergyPresent) {
        this.symptomsInputTargets[index].classList.add('required')
        symptomsElement.style.display = 'block'
      } else {
        this.symptomsInputTargets[index].classList.remove('required')
        symptomsElement.style.display = 'none'
      }

      if (!allergyPresent) {
        const input = symptomsElement.querySelector('textarea')
        if (input) input.value = ''
      }
    })
  }

  setupToggleSymptomsDisplay(selectElement) {
    selectElement.addEventListener('change', () => {
      this.toggleSymptomsDisplay()
    })

    // 初期化
    this.toggleSymptomsDisplay()
  }

  // 留学先で除去食の対応の表示/非表示を切り替える
  toggleAllergyEliminationDisplay() {
    this.allergyEliminationTargets.forEach((eliminationElement, index) => {
      const foodAllergyOrRequest =
        this.allergySelectTargets[index]?.value === '有' || this.foodrequestSelectTargets[index]?.value === '有'

      if (foodAllergyOrRequest) {
        this.allergyEliminationInputTargets[index].classList.add('required')
        eliminationElement.style.display = 'block'
      } else {
        this.allergyEliminationInputTargets[index].classList.remove('required')
        eliminationElement.style.display = 'none'
      }

      // どちらも'無'の場合にデータリセット
      const selectElement = eliminationElement.querySelector('select')
      if (!foodAllergyOrRequest) {
        if (selectElement) {
          selectElement.value = ''
        }
      }
    })
  }

  setupToggleAllergyEliminationDisplay(selectElement) {
    selectElement.addEventListener('change', () => {
      this.toggleAllergyEliminationDisplay()
    })

    // 初期化
    this.toggleAllergyEliminationDisplay()
  }

  // 保育園・幼稚園情報の表示/非表示を切り替える
  setupTogglePreschoolInfo(selectElement, index) {
    selectElement.addEventListener('change', () => {
      this.togglePreschoolInfo(selectElement, index)
    })

    // 初期化
    this.togglePreschoolInfo(selectElement, index)
  }

  togglePreschoolInfo(selectElement, index) {
    const preschoolInfoElement = this.preschoolInfoTargets[index]
    const preschoolNameInput = this.preschoolNameInputTargets[index]

    // 英語ロケールの場合のみ「Yes」が選択された場合に表示
    const shouldShow = i18n.locale === 'en' && selectElement.value === '有'

    if (shouldShow) {
      preschoolNameInput.classList.add('required')
      preschoolInfoElement.style.display = 'block'
    } else {
      preschoolNameInput.classList.remove('required')
      preschoolInfoElement.style.display = 'none'

      // フィールドをクリア
      preschoolNameInput.value = ''
      if (this.preschoolUrlInputTargets[index]) {
        this.preschoolUrlInputTargets[index].value = ''
      }
    }
  }

  // 「その他食材」の入力欄の表示/非表示を切り替える
  toggleOtherFoodInput(groupIndex) {
    const group = this.allergenicFoodsGroupTargets[groupIndex]
    const otherSelected = Array.from(group.querySelectorAll('input[type="checkbox"]')).some(
      (food) => food.value === 'その他' && food.checked,
    )

    const otherFoodInput = this.otherFoodInputTargets[groupIndex]
    if (otherFoodInput) {
      otherFoodInput.style.display = otherSelected ? 'block' : 'none'
      otherSelected
        ? otherFoodInput.querySelector('input').classList.add('required')
        : otherFoodInput.querySelector('input').classList.remove('required')
    }
  }

  enableSubmitButton() {
    const button = document.querySelector('button[data-action="click->new-reservation#validateForm"]')
    if (button) {
      this.submitButtonTarget = button
      this.submitButtonTarget.disabled = false
    }
  }

  firstFocusAction(firstFocus, element) {
    if (!firstFocus) {
      firstFocus = true
      element.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    }
    return firstFocus
  }

  addErrorMessage = (field, isValid, firstFocus, messageClass, message) => {
    // 既に同じクラスのエラーメッセージが存在する場合は追加しない
    const existingWarning = field.parentNode.querySelector(`.${messageClass}`)
    if (existingWarning) {
      return { isValid, firstFocus }
    }

    field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
    const warningNode = document.createElement('p')
    warningNode.classList.add('mt-2', 'validation-warning', messageClass, 'text-red-600', 'text-sm')
    warningNode.textContent = message
    field.parentNode.insertBefore(warningNode, field.nextSibling)
    if (field.previousElementSibling) {
      field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
    }
    isValid = false
    firstFocus = this.firstFocusAction(firstFocus, field)
    return { isValid, firstFocus }
  }

  removeErrorMessage = (field, messageClass) => {
    const existingWarning = field.parentNode.querySelector(messageClass)
    if (existingWarning) {
      existingWarning.remove()
    }
    // エラーメッセージ表示に使用したクラスも削除する
    field.classList.remove('element-validation-warning', 'border-red-600', 'bg-red-50')
    if (field.previousElementSibling) {
      field.previousElementSibling.classList.remove('text-validation-warning', 'text-red-600')
    }
  }

  validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  async validateEmail(event) {
    event.preventDefault()
    let isValid = true
    let firstFocus = false
    const field = event.target

    // メール形式チェック
    this.removeErrorMessage(field, '.validation-email-format')
    if (field.value.trim() && !field.value.match(this.validEmailRegex)) {
      const message = '有効なメールアドレスを入力してください'
      const result = this.addErrorMessage(field, isValid, firstFocus, 'validation-email-format', message)
      isValid = result.isValid
      firstFocus = result.firstFocus
    }
  }

  validPasswordRegex = /^(?=.*[a-z])(?=.*\d)[\x21-\x7E]{8,}$/
  // 半角スペース・全角文字・絵文字など、パスワードに使用できない文字
  invalidPasswordCharRegex = /[^\x21-\x7E]/
  maxPasswordLength = 64

  // パスワードの検証を1箇所に集約する。入力時（validatePassword /
  // validatePasswordConfirmation）と送信時（validateForm）で同じ判定を使うため、
  // ここを直せば3箇所すべてに効く。
  //
  // 判定順はサーバー側（app/models/user.rb の password_complexity）と同じく
  // 文字数上限 -> 使用できない文字 -> 形式 で、最初に当たったものだけを返す。
  // 問題が無ければ null を返す。
  //
  // 長さは [...value] でコードポイント数を数える。value.length は UTF-16 のコードユニット数で、
  // サーバー側（String#length）とズレるため（絵文字33個で JS 66 / Ruby 33）
  passwordError(value) {
    if (!value.trim()) return null

    if ([...value].length > this.maxPasswordLength) {
      return { messageKey: 'message.error.input.password_too_long', suffix: 'too-long' }
    }
    if (this.invalidPasswordCharRegex.test(value)) {
      return { messageKey: 'message.error.input.password_invalid_characters', suffix: 'invalid-chars' }
    }
    if (!this.validPasswordRegex.test(value)) {
      return { messageKey: 'message.error.input.valid_enter', suffix: 'regex' }
    }
    return null
  }

  validatePassword(event) {
    event.preventDefault()
    let isValid = true
    let firstFocus = false
    const field = event.target

    this.removeErrorMessage(field, '.validation-password-too-long')
    this.removeErrorMessage(field, '.validation-password-invalid-chars')
    this.removeErrorMessage(field, '.validation-password-regex')

    const error = this.passwordError(field.value)
    if (error) {
      const fieldName = field.getAttribute('data-validation-name')
      const message = i18n.t(error.messageKey, { field_name: fieldName })
      const result = this.addErrorMessage(field, isValid, firstFocus, `validation-password-${error.suffix}`, message)
      isValid = result.isValid
      firstFocus = result.firstFocus
    }
  }

  validatePasswordConfirmation(event) {
    event.preventDefault()
    let isValid = true
    let firstFocus = false
    const field = event.target

    this.removeErrorMessage(field, '.validation-password-confirmation-too-long')
    this.removeErrorMessage(field, '.validation-password-confirmation-invalid-chars')
    this.removeErrorMessage(field, '.validation-password-confirmation-regex')
    this.removeErrorMessage(field, '.validation-password-confirmation-match')

    const error = this.passwordError(field.value)
    if (error) {
      const fieldName = field.getAttribute('data-validation-name')
      const message = i18n.t(error.messageKey, { field_name: fieldName })
      const result = this.addErrorMessage(field, isValid, firstFocus, `validation-password-confirmation-${error.suffix}`, message)
      isValid = result.isValid
      firstFocus = result.firstFocus
    }

    if (this.formTarget.querySelector('#password').value !== this.formTarget.querySelector('#password_confirmation').value) {
      const fieldName = field.getAttribute('data-validation-name')
      const message = i18n.t('message.error.input.password_mismatch', { field_name: fieldName })
      const result = this.addErrorMessage(field, isValid, firstFocus, 'validation-password-confirmation-match', message)
      isValid = result.isValid
      firstFocus = result.firstFocus
    }
  }

  async validateForm(event) {
    event.preventDefault()
    let isValid = true
    this.formTarget.querySelectorAll('.validation-warning').forEach((e) => e.remove())
    this.formTarget.querySelectorAll('.text-validation-warning').forEach((e) => e.classList.remove('text-red-600'))
    this.formTarget
      .querySelectorAll('.element-validation-warning')
      .forEach((e) => e.classList.remove('border-red-600', 'bg-red-50'))

    let firstFocus = false
    const requiredFields = this.formTarget.querySelectorAll('textarea.required, input.required, select.required')
    requiredFields.forEach((field) => {
      // For each required field, check to see if the value is empty
      // if so, we focus the field and set our value to false
      if (!field.disabled && !field.value.trim()) {
        field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        const fieldName = field.getAttribute('data-validation-name')
        if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
          warningNode.textContent = i18n.t('message.error.input.enter', { field_name: fieldName })
        } else {
          // field is a select
          warningNode.textContent = i18n.t('message.error.input.select', { field_name: fieldName })
        }

        field.parentNode.insertBefore(warningNode, field.nextSibling)
        if (field.previousElementSibling) {
          field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, field)
      }
    })

    const requiredRadioGroups = this.formTarget.querySelectorAll('.radio-group.required')
    requiredRadioGroups.forEach((radioGroup) => {
      const radioInputs = radioGroup.querySelectorAll('input')
      let radioValid = false
      radioInputs.forEach((radioInput) => {
        if (!radioInput.disabled && radioInput.checked) {
          radioValid = true
        }
      })
      if (!radioValid) {
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        if (radioGroup.classList.contains('working-together')) {
          warningNode.textContent = i18n.t('message.error.input.select', {
            field_name: i18n.t('reservation.representative.occupation.income'),
          })
        } else {
          warningNode.textContent = i18n.t('message.error.input.select', {
            field_name: i18n.t('reservation_input.representative.gender.title'),
          })
        }
        radioGroup.parentNode.insertBefore(warningNode, radioGroup.nextSibling)
        if (radioGroup.previousElementSibling) {
          radioGroup.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, radioGroup)
      }
    })

    const requiredCheckboxGroups = this.formTarget.querySelectorAll('.checkbox-group.required')
    requiredCheckboxGroups.forEach((checkboxGroup) => {
      const checkboxes = checkboxGroup.querySelectorAll('input[type="checkbox"]')
      const checkboxValid = Array.from(checkboxes).some((checkbox) => checkbox.checked)

      if (!checkboxValid) {
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        const fieldName = checkboxGroup.getAttribute('data-validation-name')
        warningNode.textContent = i18n.t('message.error.input.check', { field_name: fieldName })

        checkboxGroup.parentNode.insertBefore(warningNode, checkboxGroup.nextSibling)
        if (checkboxGroup.previousElementSibling) {
          checkboxGroup.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, checkboxGroup)
      }
    })

    const dateSelectionGroups = this.formTarget.querySelectorAll('.date-selection.required')
    dateSelectionGroups.forEach((dateSelectionGroup) => {
      const dateInputs = dateSelectionGroup.querySelectorAll('select')
      let dateValid = true
      const nextSibling = dateSelectionGroup.nextSibling
      let isFirst = true
      dateInputs.forEach((dateInput) => {
        if (!dateInput.disabled && !dateInput.value.trim()) {
          dateValid = false
          dateInput.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
          const warningNode = document.createElement('p')
          warningNode.classList.add('validation-warning', 'text-red-600', 'text-sm')
          if (isFirst) {
            warningNode.classList.add('mt-2')
            isFirst = false
          }
          const fieldName = dateInput.getAttribute('data-validation-name')
          warningNode.textContent = '•  ' + i18n.t('message.error.input.enter', { field_name: fieldName })
          dateSelectionGroup.parentNode.insertBefore(warningNode, nextSibling)
        }
      })
      if (!dateValid) {
        if (dateSelectionGroup.previousElementSibling) {
          dateSelectionGroup.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, dateSelectionGroup)
      }
    })

    const numberFields = this.formTarget.querySelectorAll('input.number-only')
    numberFields.forEach((field) => {
      const isEmpty = !field.value.trim()
      const isRequired = field.classList.contains('required')

      // 未入力チェック（必須フィールドの場合）
      if (isRequired && isEmpty) {
        field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        const fieldName = field.getAttribute('data-validation-name')
        warningNode.textContent = i18n.t('message.error.input.enter', { field_name: fieldName })
        field.parentNode.insertBefore(warningNode, field.nextSibling)
        if (field.previousElementSibling) {
          field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, field)
      } else if (field.value.trim() && !this.isValidInput(field.value.trim())) {
        const validationClass = field.classList.contains('post-code') ? '.validation-postcode-number' : '.validation-phone-number'
        const messageClass = field.classList.contains('post-code') ? 'validation-postcode-number' : 'validation-phone-number'

        // 既存のエラーメッセージを削除
        this.removeErrorMessage(field, validationClass)

        field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', messageClass, 'text-red-600', 'text-sm')
        const fieldName = field.getAttribute('data-validation-name')
        if (field.classList.contains('post-code')) {
          warningNode.textContent = i18n.t('message.error.input.valid_enter', {
            field_name: i18n.t('reservation_input.representative.address.postal_code'),
          })
        } else {
          warningNode.textContent = i18n.t('message.error.input.valid_enter', { field_name: fieldName })
        }
        field.parentNode.insertBefore(warningNode, field.nextSibling)
        if (field.previousElementSibling) {
          field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, field)
      }
    })

    const emailFields = this.formTarget.querySelectorAll('input.email')
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    emailFields.forEach((field) => {
      if (field.type === 'hidden') return

      if (field.value.trim() && !field.value.match(validRegex)) {
        field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        const fieldName = field.getAttribute('data-validation-name')
        warningNode.textContent = warningNode.textContent = i18n.t('message.error.input.valid_enter', { field_name: fieldName })
        field.parentNode.insertBefore(warningNode, field.nextSibling)
        if (field.previousElementSibling) {
          field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, field)
      }
    })

    const passwordFields = this.formTarget.querySelectorAll('input.password')
    const passwordConfirmationFields = this.formTarget.querySelectorAll('input.password_confirmation')
    passwordFields.forEach((field) => {
      const error = this.passwordError(field.value)
      if (!error) return

      field.classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
      const warningNode = document.createElement('p')
      warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
      const fieldName = field.getAttribute('data-validation-name')
      warningNode.textContent = i18n.t(error.messageKey, { field_name: fieldName })
      field.parentNode.insertBefore(warningNode, field.nextSibling)
      if (field.previousElementSibling) {
        field.previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
      }
      isValid = false
      firstFocus = this.firstFocusAction(firstFocus, field)
    })

    // パスワードとパスワード確認の一致チェック
    if (passwordFields.length > 0 && passwordConfirmationFields.length > 0) {
      const password = passwordFields[0].value
      const passwordConfirmation = passwordConfirmationFields[0].value

      if (password !== passwordConfirmation) {
        passwordConfirmationFields[0].classList.add('element-validation-warning', 'border-red-600', 'bg-red-50')
        const warningNode = document.createElement('p')
        warningNode.classList.add('mt-2', 'validation-warning', 'text-red-600', 'text-sm')
        warningNode.textContent = i18n.t('message.error.input.password_mismatch')
        passwordConfirmationFields[0].parentNode.insertBefore(warningNode, passwordConfirmationFields[0].nextSibling)
        if (passwordConfirmationFields[0].previousElementSibling) {
          passwordConfirmationFields[0].previousElementSibling.classList.add('text-validation-warning', 'text-red-600')
        }
        isValid = false
        firstFocus = this.firstFocusAction(firstFocus, passwordConfirmationFields[0])
      }
    }

    // 週３日以上の通園経験の有無選択のチェック
    if (this.schoolExperienceValue) {
      const selectElement = this.opportunitynurseryschoolInputTargets[0]
      if (selectElement.value === '無') {
        const message = i18n.t('reservation.daycare_experience_modal.validation_text')
        this.addErrorMessage(selectElement, false, false, 'validation-daycare-experience', message)
        isValid = false
      }
    }

    if (!isValid) {
      // エラーがある最初のアコーディオンを開いてスクロール
      this.openFirstInvalidAccordion()
      return
    }
    this.formTarget.submit()
  }

  isValidInput(value) {
    // 言語が英語なら英数字を許可、それ以外は数字のみ
    if (i18n.locale === 'en') {
      return /^[a-zA-Z0-9]+$/.test(value) // 英数字を許可
    } else {
      return /^[0-9]+$/.test(value) // 数字のみ許可
    }
  }

  optionChange() {
    let optionFee = 0
    for (let i = 0; i < this.optionTargets.length; i++) {
      const optionPrice = parseInt(this.optionTargets[i].getAttribute('data-price'))
      optionFee = optionFee + this.optionTargets[i].value * optionPrice
    }
    optionFee = parseInt(optionFee.toString().split('.')[0])
    this.optionFeeTarget.textContent = i18n.t('price_text', { price: optionFee.toLocaleString('en-US') })
    this.totalFeeTargets.forEach((el) => {
      el.textContent = i18n.t('price_text', { price: (parseInt(this.baseFeeValue) + optionFee).toLocaleString('en-US') })
    })

    this.optionFeeInputTarget.value = optionFee
  }

  togglePrefectureField() {
    const country = this.countryTarget.value
    const prefectureFormTarget = this.prefectureFormTarget

    // 既存の都道府県フォームを削除
    while (prefectureFormTarget.lastChild) {
      prefectureFormTarget.removeChild(prefectureFormTarget.lastChild)
    }

    if (country === 'Japan') {
      const paragraph = document.createElement('p')
      paragraph.textContent = ' State/Province '
      paragraph.className = 'font-medium text-sm pt-7'
      prefectureFormTarget.appendChild(paragraph)

      const span = document.createElement('span')
      span.textContent = '*'
      span.className = 'text-red-500 ml-1'
      paragraph.appendChild(span)

      const select = document.createElement('select')
      select.name = 'fronts_reservation[representative_prefecture]'
      select.className =
        'h-10 bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 text-sm text-gray-900 w-1/2 md:w-[192px] select-check required'
      select.dataset.newReservationTarget = 'prefecture'
      select.dataset.validationName = 'State/Province'
      const defaultOption = document.createElement('option')
      defaultOption.value = ''
      defaultOption.textContent = 'Select Prefecture'
      select.appendChild(defaultOption)
      const prefectureList = [
        '北海道',
        '青森県',
        '岩手県',
        '宮城県',
        '秋田県',
        '山形県',
        '福島県',
        '茨城県',
        '栃木県',
        '群馬県',
        '埼玉県',
        '千葉県',
        '東京都',
        '神奈川県',
        '新潟県',
        '富山県',
        '石川県',
        '福井県',
        '山梨県',
        '長野県',
        '岐阜県',
        '静岡県',
        '愛知県',
        '三重県',
        '滋賀県',
        '京都府',
        '大阪府',
        '兵庫県',
        '奈良県',
        '和歌山県',
        '鳥取県',
        '島根県',
        '岡山県',
        '広島県',
        '山口県',
        '徳島県',
        '香川県',
        '愛媛県',
        '高知県',
        '福岡県',
        '佐賀県',
        '長崎県',
        '熊本県',
        '大分県',
        '宮崎県',
        '鹿児島県',
        '沖縄県',
      ]
      prefectureList.forEach((prefecture) => {
        const option = document.createElement('option')
        option.value = prefecture
        option.textContent = prefecture
        select.appendChild(option)
      })
      prefectureFormTarget.appendChild(select)
    } else {
      const paragraph = document.createElement('p')
      paragraph.textContent = ' State/Province '
      paragraph.className = 'font-medium text-sm pt-7'
      prefectureFormTarget.appendChild(paragraph)

      const span = document.createElement('span')
      span.textContent = '*'
      span.className = 'text-red-500 ml-1'
      paragraph.appendChild(span)

      const input = document.createElement('input')
      input.name = 'fronts_reservation[representative_prefecture]'
      input.className = 'text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 input-check required'
      input.dataset.newReservationTarget = 'prefecture'
      input.dataset.validationName = 'State/Province'
      prefectureFormTarget.appendChild(input)
    }
  }

  async fetchAddress() {
    const zipcode = this.postcodeTarget.value

    if (zipcode.length !== 7) return

    try {
      const response = await fetch(`/addresses/search?zipcode=${zipcode}`)
      if (!response.ok) {
        alert(i18n.t('message.error.address.load'))
        return
      }

      const data = await response.json()

      this.prefectureTarget.value = data.prefecture || ''
      this.municipalitiesTarget.value = data.municipalities || ''
      this.townTarget.value = data.town || ''
    } catch (error) {
      alert(i18n.t('message.error.address.load'))
      console.error(error)
    }
  }
}
